import {
  READINESS_PERSONAS,
  ROLE_FORMS,
  TEMPLATE_LEDGER,
  type TemplateLedger,
  defaultDimensionDisplay,
  describeAccountOverlay,
  type AccountOverlay,
  type DocumentType,
  type JournalOverlay,
  type PeopleData,
  type ReadinessCase,
  type ReadinessPersona,
  type ReadinessPlan,
  type RoutingConfig,
  type VenueRole,
} from '@lg/shared';
import { simulateRouting, type RoutingPeople } from '../domain/routing.js';

/**
 * Readiness plan generator (build update 2.4, section 4). Pure: the same request,
 * people and template always give the same cases. It writes what the venue must be able
 * to do; it never decides pass or fail.
 */

export interface GovernedSetting {
  key: string;
  label: string;
  value: string;
  templateValue: string;
  /** Approved at gate 2: expected to differ from the template */
  deviation: boolean;
}

export interface PlanInput {
  requestId: string | null;
  company: string;
  generatedAt: string;
  templateVersion: string;
  /** venue.modules answer: Procurement, AP, Projects */
  modules: readonly string[];
  people: PeopleData;
  routingPeople: RoutingPeople;
  routing: RoutingConfig;
  roleCatalog: readonly VenueRole[];
  governed: readonly GovernedSetting[];
  copiedEntitySets: readonly string[];
  /** Another venue company, for the cross-company refusal check (T-ACC-02) */
  otherCompany: string;
  /** The request's ledger overlays (T-OVL-01, T-OVL-02) */
  overlays?: { journals: readonly JournalOverlay[]; accounts: readonly AccountOverlay[] };
  /** The template company's ledger rules (overlay dimension, accounts it overrides); USMF when absent */
  ledger?: TemplateLedger;
  /** The template company, for the "template unchanged" side of T-OVL-01 */
  templateCompany?: string;
  /** Adds the seeded demo failure (section 15). Never set for real runs. */
  includeDemo?: boolean;
}

/** A persona and the seat it takes in this company, with the seat holder's role bundle. */
export interface PersonaSeat {
  persona: ReadinessPersona;
  positionId: string;
  title: string;
  holderName: string | null;
  holderUpn: string | null;
  roles: string[];
}

const BOTH: ReadinessCase['envs'] = ['TEST', 'PROD'];
const TEST: ReadinessCase['envs'] = ['TEST'];
const PROJECT_SETS = new Set(['ProjectGroups', 'ProjectCategories']);

const usd = (n: number): string => `$${n.toLocaleString('en-US')}`;

export function personaSeats(company: string, people: PeopleData): PersonaSeat[] {
  const seats: PersonaSeat[] = [];
  for (const persona of READINESS_PERSONAS) {
    const pos = people.positions.find(
      (p) => p.jobId === persona.jobId && p.positionId.toUpperCase().startsWith(`${company.toUpperCase()}-`),
    );
    if (!pos) continue;
    const a = people.assignments.find((x) => x.positionId === pos.positionId && x.primary);
    const w = a ? people.workers.find((x) => x.personnelNumber === a.personnelNumber) : undefined;
    const upn = w?.upn?.toLowerCase() ?? null;
    seats.push({
      persona,
      positionId: pos.positionId,
      title: pos.title,
      holderName: w ? `${w.firstName} ${w.lastName}` : null,
      holderUpn: upn,
      roles: upn ? people.roles.filter((r) => r.upn.toLowerCase() === upn).map((r) => r.roleId) : [],
    });
  }
  return seats;
}

/** One amount inside each approval band: 80 % of the band's top, and 20 % over the highest limit. */
export function bandAmounts(limits: readonly number[]): number[] {
  const tops = [...new Set(limits.filter((n) => n > 0))].sort((a, b) => a - b);
  const out: number[] = [];
  let bottom = 0;
  for (const top of tops) {
    const amount = Math.round((bottom + (top - bottom) * 0.8) / 100) * 100;
    out.push(Math.max(amount, bottom + 1));
    bottom = top;
  }
  if (bottom > 0) out.push(Math.round((bottom * 1.2) / 100) * 100);
  return out;
}

export function generateReadinessPlan(input: PlanInput): ReadinessPlan {
  const C = input.company.toUpperCase();
  const cases: ReadinessCase[] = [];
  const has = (m: string) => input.modules.includes(m);
  const seats = personaSeats(C, input.people);
  const seatOf = (key: string) => seats.find((s) => s.persona.key === key);
  const seatByPosition = (positionId: string) => seats.find((s) => s.positionId === positionId);
  const roleName = (id: string) => input.roleCatalog.find((r) => r.roleId === id)?.name ?? id;
  const docRoute = (type: DocumentType) =>
    input.routing.documentTypes.find((d) => d.type === type && d.inScope) ?? null;

  // ── L1 settings: one case per governed setting ────────────────────────────
  for (const g of input.governed) {
    cases.push({
      id: `${C}-CONF-01-${g.key.toUpperCase()}`,
      layer: 'L1',
      template: 'T-CONF-01',
      title:
        g.value === g.templateValue
          ? `${g.label} matches the template (${g.templateValue})`
          : g.deviation
            ? `${g.label} is ${g.value} (approved deviation from ${g.templateValue})`
            : `${g.label} is ${g.value}`,
      persona: null,
      company: C,
      data: { setting: g.key, label: g.label },
      expect: { value: g.value, templateValue: g.templateValue, approvedDeviation: g.deviation },
      envs: BOTH,
    });
  }

  // ── L4 data loads: one case per copied entity group ──────────────────────
  for (const set of input.copiedEntitySets) {
    if (PROJECT_SETS.has(set) && !has('Projects')) continue;
    cases.push({
      id: `${C}-LOAD-01-${set.toUpperCase()}`,
      layer: 'L4',
      template: 'T-LOAD-01',
      title: `${set} record count matches the template`,
      persona: null,
      company: C,
      data: { entity: set },
      expect: { matchesTemplate: true },
      envs: BOTH,
    });
  }

  // ── Ledger overlays: venue-only overrides (L1) and journal control (L3) ───
  for (const a of input.overlays?.accounts ?? []) {
    cases.push({
      id: `${C}-OVL-01-${a.mainAccountId}`,
      layer: 'L1',
      template: 'T-OVL-01',
      title: `${describeAccountOverlay(a, input.ledger).replace(' (this venue only)', '')} in ${C}; unchanged in ${input.templateCompany ?? 'the template'}`,
      persona: null,
      company: C,
      data: {
        mainAccount: a.mainAccountId,
        templateCompany: input.templateCompany ?? '',
      },
      expect: {
        suspended: a.suspend,
        defaultDimension: a.defaultDimensionValue
          ? defaultDimensionDisplay(a.defaultDimensionValue, input.ledger)
          : null,
        templateUnchanged: true,
      },
      envs: BOTH,
    });
  }
  for (const j of input.overlays?.journals ?? []) {
    // An account the template uses that falls outside the journal's range.
    const blocked =
      (input.ledger ?? TEMPLATE_LEDGER).overriddenAccounts.find(
        (x) => x < j.fromAccount || x > j.toAccount,
      ) ?? '999999';
    cases.push({
      id: `${C}-OVL-02-${j.name.toUpperCase()}`,
      layer: 'L3',
      template: 'T-OVL-02',
      title: `Journal ${j.name} blocks a line on ${blocked} (allowed ${j.fromAccount}–${j.toAccount})`,
      persona: null,
      company: C,
      data: { journalName: j.name, blockedAccount: blocked, allowedAccount: j.fromAccount },
      expect: { blockedByJournalControl: true, allowedAccountPosts: true },
      envs: TEST,
    });
  }

  // ── L2 access: per role held by at least one user ────────────────────────
  const heldRoles = [...new Set(input.people.roles.map((r) => r.roleId))].filter((id) =>
    input.roleCatalog.some((r) => r.roleId === id),
  );
  for (const roleId of heldRoles) {
    const seat = seats.find((s) => s.roles.includes(roleId));
    // A role nobody in a venue seat holds (e.g. regional finance) is covered by T-ACC-04 only.
    if (!seat) continue;
    const forms = ROLE_FORMS[roleId] ?? [];
    cases.push({
      id: `${C}-ACC-01-${roleId}`,
      layer: 'L2',
      template: 'T-ACC-01',
      title: `${seat.persona.label} opens the ${roleName(roleId)} forms in ${C}`,
      persona: seat.persona.key,
      company: C,
      data: { role: roleId, forms: forms.join('; ') },
      expect: { allFormsOpen: true, noAccessMessages: 0 },
      envs: BOTH,
    });
    cases.push({
      id: `${C}-ACC-02-${roleId}`,
      layer: 'L2',
      template: 'T-ACC-02',
      title: `${roleName(roleId)} sees only ${C}; ${input.otherCompany} is refused`,
      persona: seat.persona.key,
      company: C,
      data: { role: roleId, otherCompany: input.otherCompany },
      expect: { availableCompanies: [C], refused: input.otherCompany },
      envs: BOTH,
    });
  }
  const buyer = seatOf('buyer');
  if (buyer && has('Procurement')) {
    cases.push({
      id: `${C}-ACC-03-BUY`,
      layer: 'L2',
      template: 'T-ACC-03',
      title: `Buyer cannot post a general journal in ${C}`,
      persona: 'buyer',
      company: C,
      data: { action: 'Post a general journal' },
      expect: { blockedBySecurity: true },
      envs: TEST,
    });
  }
  cases.push({
    id: `${C}-ACC-04`,
    layer: 'L2',
    template: 'T-ACC-04',
    title: "Every user's roles match a tested persona's bundle",
    persona: null,
    company: C,
    data: { users: input.people.users.length, personas: seats.length },
    expect: { allUsersCovered: true },
    envs: BOTH,
  });

  // ── L3 process: purchase orders, one amount per approval band ─────────────
  const po = docRoute('PurchaseOrder');
  let firstApprovedPo: { approveId: string; amount: number } | null = null;
  if (buyer?.holderUpn && po && has('Procurement')) {
    const limits = input.people.approvalLimits
      .filter((l) => l.documentType === 'PurchaseOrder')
      .map((l) => l.amountUsd);
    for (const amount of bandAmounts(limits)) {
      const r = simulateRouting(
        { submitterUpn: buyer.holderUpn, documentType: 'PurchaseOrder', amountUsd: amount },
        input.routingPeople,
        input.routing,
      );
      const createId = `${C}-P2P-01-BUY-${amount}`;
      const routeId = `${C}-P2P-02-${amount}`;
      const step = r.chain.find((s) => s.kind === 'managerial' || s.kind === 'escalation');
      const approver = step?.approvers[0];
      const seatId = approver?.positionId ?? null;
      const external = seatId ? (input.routingPeople.positions.get(seatId)?.external ?? false) : false;
      const venueSeat = seatId ? seatByPosition(seatId) : undefined;
      cases.push({
        id: createId,
        layer: 'L3',
        template: 'T-P2P-01',
        title: `Buyer creates a ${usd(amount)} PO and submits it`,
        persona: 'buyer',
        company: C,
        data: { vendor: 'TST-0001', category: 'Readiness', amount },
        expect: { status: 'In review' },
        envs: TEST,
      });
      cases.push({
        id: routeId,
        layer: 'L3',
        template: 'T-P2P-02',
        title: seatId
          ? `${usd(amount)} PO routes to ${seatId} (${(approver?.title ?? '').toLowerCase()})`
          : `${usd(amount)} PO: ${r.gaps[0]?.message ?? 'no approver found'}`,
        persona: null,
        company: C,
        data: { amount },
        expect: seatId
          ? { approverSeat: seatId, approverName: approver?.name ?? null, outsideVenue: external }
          : { approverSeat: null, gap: r.gaps[0]?.message ?? 'No approver' },
        dependsOn: [createId],
        envs: TEST,
      });
      // Shared regional seats are not approved by a test persona; reaching them is the check.
      if (venueSeat && !external) {
        const approveId = `${C}-P2P-03-${venueSeat.persona.short}-${amount}`;
        cases.push({
          id: approveId,
          layer: 'L3',
          template: 'T-P2P-03',
          title: `${venueSeat.persona.label} approves the ${usd(amount)} PO`,
          persona: venueSeat.persona.key,
          company: C,
          data: { amount },
          expect: { status: 'Approved', confirmed: true },
          dependsOn: [routeId],
          envs: TEST,
        });
        firstApprovedPo ??= { approveId, amount };
      }
    }
  }

  // ── L3 vendor invoice for the first approved PO ───────────────────────────
  const vi = docRoute('VendorInvoice');
  const apClerk = seatOf('ap');
  if (vi && has('AP') && apClerk?.holderUpn && firstApprovedPo) {
    const { amount } = firstApprovedPo;
    const r = simulateRouting(
      { submitterUpn: apClerk.holderUpn, documentType: 'VendorInvoice', amountUsd: amount },
      input.routingPeople,
      input.routing,
    );
    const route = r.chain.map((s) => `${s.label}: ${s.approvers.map((a) => a.name).join(', ')}`).join(' → ');
    const invoiceId = `${C}-AP-01-AP-${amount}`;
    cases.push({
      id: invoiceId,
      layer: 'L3',
      template: 'T-AP-01',
      title: `AP clerk invoices the ${usd(amount)} PO and submits it`,
      persona: 'ap',
      company: C,
      data: { amount },
      expect: { routedAs: route || (r.gaps[0]?.message ?? 'no route') },
      dependsOn: [firstApprovedPo.approveId],
      envs: TEST,
    });
    const reviewRole = vi.steps.find((s) => s.kind === 'role');
    const approverSeat = reviewRole ? seats.find((s) => s.roles.includes(reviewRole.roleId)) : undefined;
    if (approverSeat) {
      cases.push({
        id: `${C}-AP-02-${approverSeat.persona.short}-${amount}`,
        layer: 'L3',
        template: 'T-AP-02',
        title: `${approverSeat.persona.label} approves the ${usd(amount)} invoice`,
        persona: approverSeat.persona.key,
        company: C,
        data: { amount },
        expect: { status: 'Approved' },
        dependsOn: [invoiceId],
        envs: TEST,
      });
    }
  }

  // ── L3 journal approval ───────────────────────────────────────────────────
  const jnl = docRoute('Journal');
  const glRole = jnl?.steps.find((s) => s.kind === 'role');
  const glSeat = glRole ? seats.find((s) => s.roles.includes(glRole.roleId)) : undefined;
  if (glSeat) {
    cases.push({
      id: `${C}-GL-01-${glSeat.persona.short}`,
      layer: 'L3',
      template: 'T-GL-01',
      title: `${glSeat.persona.label} approves a submitted journal`,
      persona: glSeat.persona.key,
      company: C,
      data: { journal: 'Created by setup, unposted' },
      expect: { status: 'Approved' },
      envs: TEST,
    });
  }

  // ── L3 projects ───────────────────────────────────────────────────────────
  const pm = seatOf('pm');
  if (has('Projects') && pm) {
    const dimension = input.governed.find((g) => g.key === 'venueDimension')?.value ?? C;
    cases.push({
      id: `${C}-PRJ-01-PM`,
      layer: 'L3',
      template: 'T-PRJ-01',
      title: "Project manager creates a project in the venue's project group",
      persona: 'pm',
      company: C,
      data: { projectGroup: 'VENUE' },
      expect: { created: true, venueDimension: dimension },
      envs: TEST,
    });
  }

  // ── seeded demo failure (section 15): a form outside the buyer's bundle ───
  if (input.includeDemo && buyer) {
    cases.push({
      id: `${C}-ACC-01-DEMO`,
      layer: 'L2',
      template: 'T-ACC-01',
      title: 'Buyer opens General journals (outside the buyer bundle)',
      persona: 'buyer',
      company: C,
      data: { role: 'VENDPURCHASINGAGENT', forms: 'General journals' },
      expect: { allFormsOpen: true },
      envs: TEST,
      demo: true,
    });
  }

  return {
    requestId: input.requestId,
    company: C,
    generatedAt: input.generatedAt,
    templateVersion: input.templateVersion,
    cases,
  };
}
